import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import { fontSize, iconSize, spacing, } from "../../../../core/design-system/index.js";
import { useWalletBalance } from "../../../../core/hooks/others/useWalletBalance.js";
import { useActiveAccount } from "../../../../core/hooks/wallets/useActiveAccount.js";
import { useActiveWalletChain } from "../../../../core/hooks/wallets/useActiveWalletChain.js";
import { useSendToken } from "../../../../core/hooks/wallets/useSendToken.js";
import { defaultTokens, } from "../../../../core/utils/defaultTokens.js";
import { Skeleton } from "../../components/Skeleton.js";
import { Spacer } from "../../components/Spacer.js";
import { Spinner } from "../../components/Spinner.js";
import { TokenIcon } from "../../components/TokenIcon.js";
import { Container, ModalHeader } from "../../components/basic.js";
import { Button } from "../../components/buttons.js";
import { Input, Label } from "../../components/formElements.js";
import { Text } from "../../components/text.js";
import { StyledDiv } from "../../design-system/elements.js";
import { TokenSelector } from "./TokenSelector.js";
import { formatTokenBalance } from "./formatTokenBalance.js";
import { NATIVE_TOKEN } from "./nativeToken.js";
/**
 * @internal
 */
export function SendFunds(props) {
    const [screen, setScreen] = useState("base");
    const activeChain = useActiveWalletChain();
    const chainId = activeChain?.id;
    const { connectLocale, client } = props;
    let defaultToken = NATIVE_TOKEN;
    const supportedTokens = props.supportedTokens || defaultTokens;
    if (
    // if we know chainId
    chainId &&
        // if there is a list of tokens for this chain
        supportedTokens[chainId] &&
        // if the list of tokens is not the default list
        supportedTokens[chainId] !== defaultTokens[chainId]) {
        // use the first token in the list as default selected
        const tokensForChain = supportedTokens[chainId];
        const firstToken = tokensForChain?.[0];
        if (firstToken) {
            defaultToken = firstToken;
        }
    }
    const [token, setToken] = useState(defaultToken);
    const [receiverAddress, setReceiverAddress] = useState("");
    const [amount, setAmount] = useState("0");
    const chain = useActiveWalletChain();
    const tokenList = (chain?.id ? supportedTokens[chain.id] : undefined) || [];
    if (screen === "tokenSelector" && chain) {
        return (_jsx(TokenSelector, { tokenList: tokenList, onBack: () => {
                setScreen("base");
            }, onTokenSelect: (_token) => {
                setToken(_token);
                setScreen("base");
            }, chain: chain, connectLocale: connectLocale, client: client }));
    }
    return (_jsx(SendFundsForm, { token: token, onTokenSelect: () => {
            setScreen("tokenSelector");
        }, receiverAddress: receiverAddress, setReceiverAddress: setReceiverAddress, amount: amount, setAmount: setAmount, onBack: props.onBack, client: client, connectLocale: connectLocale }));
}
/**
 * @internal Exported for tests
 */
export function SendFundsForm(props) {
    const locale = props.connectLocale.sendFundsScreen;
    const tokenAddress = props.token && "address" in props.token ? props.token.address : undefined;
    const chain = useActiveWalletChain();
    const activeAccount = useActiveAccount();
    const activeChain = useActiveWalletChain();
    const balanceQuery = useWalletBalance({
        chain,
        tokenAddress: tokenAddress,
        address: activeAccount?.address,
        client: props.client,
    });
    const { receiverAddress, setReceiverAddress, amount, setAmount } = props;
    const sendTokenMutation = useSendToken(props.client);
    function getErrorMessage(error) {
        const message = error?.data?.message || error?.message;
        if (!message) {
            return locale.transactionFailed;
        }
        if (message.includes("user rejected") ||
            message.includes("user closed modal") ||
            message.includes("user denied")) {
            return locale.transactionRejected;
        }
        if (message.includes("insufficient funds")) {
            return locale.insufficientFunds;
        }
        return message;
    }
    if (!activeChain) {
        return null; // this should never happen
    }
    if (sendTokenMutation.isError) {
        return (_jsxs(Container, { p: "lg", animate: "fadein", children: [_jsx(ModalHeader, { title: locale.title, onBack: () => {
                        sendTokenMutation.reset();
                    } }), _jsx(Spacer, { y: "xl" }), _jsxs(Container, { flex: "column", gap: "lg", animate: "fadein", center: "both", style: {
                        minHeight: "200px",
                    }, color: "danger", children: [_jsx(CrossCircledIcon, { width: iconSize.xl, height: iconSize.xl }), _jsx(Text, { center: true, multiline: true, color: "danger", children: getErrorMessage(sendTokenMutation.error) })] })] }));
    }
    if (sendTokenMutation.isSuccess) {
        return (_jsxs(Container, { p: "lg", animate: "fadein", children: [_jsx(ModalHeader, { title: locale.title, onBack: () => {
                        sendTokenMutation.reset();
                    } }), _jsxs(Container, { flex: "column", gap: "lg", animate: "fadein", center: "both", style: {
                        minHeight: "250px",
                    }, color: "success", children: [_jsx(CheckCircledIcon, { width: iconSize.xl, height: iconSize.xl }), _jsxs(Text, { color: "success", children: [" ", locale.successMessage, " "] })] })] }));
    }
    const tokenName = (props.token && "name" in props.token ? props.token.name : undefined) ||
        balanceQuery?.data?.name;
    const tokenSymbol = (props.token && "symbol" in props.token ? props.token.symbol : undefined) ||
        balanceQuery?.data?.symbol;
    return (_jsxs(Container, { p: "lg", animate: "fadein", children: [_jsx(ModalHeader, { title: locale.title, onBack: props.onBack }), _jsx(Spacer, { y: "xl" }), _jsxs("form", { onSubmit: (e) => {
                    e.preventDefault();
                }, children: [_jsx(Label, { htmlFor: "token", color: "secondaryText", children: locale.token }), _jsx(Spacer, { y: "sm" }), _jsxs(Button, { id: "token", variant: "outline", fullWidth: true, style: {
                            justifyContent: "flex-start",
                            gap: spacing.sm,
                            padding: spacing.sm,
                        }, onClick: props.onTokenSelect, children: [_jsx(TokenIcon, { token: props.token, chain: activeChain, size: "lg", client: props.client }), _jsxs(Container, { flex: "column", gap: "xs", children: [tokenName ? (_jsx(Text, { size: "sm", color: "primaryText", children: tokenName })) : (_jsx(Skeleton, { height: fontSize.xs, width: "150px" })), balanceQuery.data ? (_jsx(Text, { size: "xs", children: formatTokenBalance(balanceQuery.data) })) : (_jsx(Skeleton, { height: fontSize.xs, width: "100px" }))] })] }), _jsx(Spacer, { y: "lg" }), _jsx(Label, { htmlFor: "receiver", color: "secondaryText", children: locale.sendTo }), _jsx(Spacer, { y: "sm" }), _jsx(Input, { required: true, id: "receiver", placeholder: "0x... or ENS name", variant: "outline", value: receiverAddress, onChange: (e) => {
                            setReceiverAddress(e.target.value);
                        } }), _jsx(Spacer, { y: "lg" }), _jsx(Label, { htmlFor: "amount", color: "secondaryText", children: locale.amount }), _jsx(Spacer, { y: "sm" }), _jsxs(Container, { relative: true, children: [_jsx(Input, { required: true, type: "number", id: "amount", variant: "outline", value: amount, onChange: (e) => {
                                    setAmount(e.target.value);
                                } }), _jsx(CurrencyBadge, { children: _jsxs(Text, { size: "xs", children: [" ", tokenSymbol, " "] }) })] }), _jsx(Spacer, { y: "xxl" }), _jsxs(Button, { fullWidth: true, variant: "accent", type: "submit", className: "tw-sendfunds-screen-send-button", onClick: async () => {
                            if (!receiverAddress || !amount) {
                                return;
                            }
                            await sendTokenMutation.mutateAsync({
                                receiverAddress,
                                amount,
                                tokenAddress: tokenAddress,
                            });
                        }, style: {
                            alignItems: "center",
                            gap: spacing.sm,
                            padding: spacing.md,
                        }, children: [sendTokenMutation.isPending && (_jsx(Spinner, { size: "sm", color: "accentButtonText" })), sendTokenMutation.isPending ? locale.sending : locale.submitButton] })] })] }));
}
const CurrencyBadge = /* @__PURE__ */ StyledDiv({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: spacing.sm,
});
//# sourceMappingURL=SendFunds.js.map